import { workspace } from '../../shared/api';
import { PRESCRIPTION_STATUS } from '../../interfaces/prescription';
import { ManualMedicine, Medicine, PT_PRESCRIPTION_TYPE } from '../../interfaces/medicine';
import { Exam } from '../../interfaces/exam';
import { MedicalCertificate } from '../../interfaces/medicalCertificate';
import { MedicalReferral } from '../../interfaces/MedicalReferral';
import { MedicalReport } from '../../interfaces/medicalReport';
import { CANCELLABLE_DOCS } from '../../components/CancelByDocument';
import { DialogContent } from '../../shared/prescriber';
import { Formula, FormulaSummary } from '../../interfaces/formula';

interface ModelDetailFormula {
  id: number;
  idPrescricao: number;
  NomeFormula: string;
  ComposicaoEstruturada?: string;
  Posologia: string;
  Quantidade: number;
  SimboloMedida: string;
  NomeFormaFisica: string;
  TipoReceita: string;
  Composicao?: string;
  AdicionadoPorIA?: boolean;
  IdExterno?: string | null;
  Ordem?: number | null;
  Origem?: string | null;
}

// Adaptar ModelDetailFormula (formato da API) para Formula (formato esperado pelo FormulaSummaryCard)
const adaptModelFormulaToFormula = (formula: ModelDetailFormula, doctorId: number = 0): Formula => {
  return {
    id: formula.id,
    doctorId: doctorId,
    formulaName: formula.NomeFormula,
    structuredComposition: formula.ComposicaoEstruturada,
    posology: formula.Posologia,
    quantity: formula.Quantidade,
    measure: formula.SimboloMedida,
    physicalForm: formula.NomeFormaFisica,
    prescriptionType: formula.TipoReceita,
    composition: formula.Composicao || '',
  };
};

export const ModelService = {
  async fetchModels(
    doctorId: number,
    prescriptionId: number,
    listHighlighted: boolean,
    extraParams: Record<string, any> = {}
  ): Promise<ModelListResponse> {
    const params = {
      idMedico: doctorId,
      idPrescricao: prescriptionId,
      ...(listHighlighted && { Destacados: listHighlighted }),
      ...extraParams,
    };

    const { data } = await workspace.get('v2/modeloprescricao/consultar', { params });

    return data;
  },
  async fetchSponsoredModels(doctorId: number, prescriptionId: number, sponsored: boolean = false): Promise<ModelListResponse> {
    const params = {
      idMedico: doctorId,
      idPrescricao: prescriptionId,
      sponsored,
    };

    const { data } = await workspace.get('v2/modeloprescricao/patrocinados', { params });

    return data;
  },
  async deleteModel(modelPrescriptionId: number) {
    const params = {
      idPrescricaoModelo: modelPrescriptionId,
    };

    const { data } = await workspace.delete('/modeloprescricao/remover', { params });
    return data;
  },
  async fetchModelDetails(modelId: number, prescriptionId: number, doctorId?: number): Promise<ModelDetails> {
    const params = {
      idPrescricaoModelo: modelId,
      idPrescricao: prescriptionId,
    };

    const { data } = await workspace.get('/modeloprescricao/detalhes', { params });

    return {
      ...data,
      MedicamentoManual: Array.isArray(data.MedicamentoManual)
        ? data.MedicamentoManual.map(adaptModelManualMedicineToManualMedicine)
        : [],
      Medicamento: Array.isArray(data.Medicamento) ? data.Medicamento : [],
      Exame: Array.isArray(data.Exame) ? data.Exame : [],
      Atestados: Array.isArray(data.Atestados) ? data.Atestados : null,
      Encaminhamentos: Array.isArray(data.Encaminhamentos) ? data.Encaminhamentos : null,
      Formulas: Array.isArray(data.FormulasManipuladas)
        ? data.FormulasManipuladas.map((formula: ModelDetailFormula) => adaptModelFormulaToFormula(formula, doctorId))
        : [],
    };
  },
  async createModel(prescriptionId: number, modelName: string, isInstitutional: boolean): Promise<CreateModelResponse> {
    const body = {
      idPrescricao: prescriptionId,
      Nome: modelName,
      ModeloInstitucional: isInstitutional,
    };

    const { data } = await workspace.post('/modeloprescricao', body);
    return data;
  },
  async updatePrescriberDialogPharmaModels(pharmaModel: DialogContent, prescriberId: number): Promise<void> {
    await workspace.put(`/medico/${prescriberId}/configuracoes`, {
      dialogs: { pharmaModel },
    });
  },
};

export const HistoryService = {
  async fetchPrescriptionHistory(payload: {
    patientId: number;
    prescriberId: number;
    establishmentId: number;
    page: number;
    prescriptionId: number;
  }): Promise<PrescriptionHistory> {
    const params = {
      idPaciente: payload.patientId,
      idMedico: payload.prescriberId,
      idEstabelecimento: payload.establishmentId,
      idPrescricao: payload.prescriptionId,
      pageSize: '10',
      page: payload.page,
    };

    const { data } = await workspace.get(`/pacientes/${params.idPaciente}/prescricoes`, { params });

    const mappedPrescriptionHistory = data.Prescricoes.map((item: HistoryPrescription) => {
      return {
        ...item,
      };
    });

    return {
      Medico: data.Medico,
      Prescricoes: mappedPrescriptionHistory,
      PossuiProximaPagina: data.PossuiProximaPagina,
    };
  },
  async cancelPrescriptionHistory(
    reason: string,
    prescriptionId: number,
    prescriptionToBeCanceled: number
  ): Promise<void> {
    await workspace.delete(`/prescricao/${prescriptionId}/historico`, {
      data: { motivoCancelamento: reason || '', idPrescricaoACancelar: prescriptionToBeCanceled },
    });
  },

  async cancelPrescriptionHistoryByDocument(
    prescriptionToBeCanceled: number,
    documentsToBeCancelled: CANCELLABLE_DOCS[]
  ): Promise<void> {
    await workspace.delete(`/prescricao/${prescriptionToBeCanceled}/documentos`, {
      data: { tiposDocumento: documentsToBeCancelled },
    });
  },
};

const adaptModelManualMedicineToManualMedicine = (manualMedicine: ModelDetailManualMedicine): ManualMedicine => {
  return {
    idPrescricaoMedicamentoManual: manualMedicine.idPrescricaoMedicamentoManual,
    idMedicamentoManual: null,
    UsoContinuo: manualMedicine.UsoContinuo,
    Posologia: manualMedicine.Posologia,
    Label: manualMedicine.NomeMedicamento,
    DetalhesDispensa: [],
    Dispensado: false,
    QuantidadeDispensada: null,
    Nome: manualMedicine.NomeMedicamento,
    Manual: true,
    Quantidade: null,
    Embalagem: null,
    UsoInterno: manualMedicine.UsoInterno,
    LME: false,
    ProdutoCorrelato: false,
    QuantidadeUnidadeFormatada: manualMedicine.QuantidadeUnidadeFormatada,
    Ordem: manualMedicine.Ordem,
    Manipulado: manualMedicine.Manipulado,
    TipoReceita: manualMedicine.TipoReceita,
  };
};

export interface ModelListResponse {
  administradorModeloInstitucional: boolean;
  data: Array<Model>;
}

export interface HistoryPrescriptionStatus {
  idPrescricaoStatus: PRESCRIPTION_STATUS;
  Descricao: string;
}

export interface HistoryMedicine {
  idPrescricaoMedicamento: number;
  idMedicamento: number;
  Comentario: string;
  Posologia: string;
  UsoContinuo: boolean;
  Quantidade: number;
  QuantidadeEmbalagens: string;
  Medicamento: HistoryMedicine2;
  medicamentoLme: HistoryMedicineLme | null;
  Ordem: number | null;
}

export interface HistoryMedicineLme {
  cid: string;
  unidades: {
    unidade: number;
    mes: number;
  }[];
}

export interface HistoryMedicine2 {
  idMedicamento: number;
  idPortaria344: number;
  idTipoMedicamento: number;
  ControleEspecial: boolean;
  Nome: string;
  Concentracao: string;
  Unidade: string;
}

export interface HistoryManualMedicine {
  Nome: string;
  Comentario: string;
  idMedicamentoManual: number;
  Label: string;
  idPrescricaoMedicamentoManual: number;
  Quantidade: number;
  Posologia: string;
  Embalagem: string;
  UsoContinuo: boolean;
  UsoInterno: boolean;
  Ordem: number | null;

  // esses campos são fixos, como tem a ver com dispensação não vamos preencher
  DetalhesDispensa: [];
  QuantidadeDispensada: 0;
  Dispensado: false;
}

export interface HistoryPrescription {
  idPrescricao: number;
  CodigoValidacao: string;
  UrlQrCode: string;
  Status: HistoryPrescriptionStatus;
  Medicamentos: HistoryMedicine[];
  MedicamentosManuais: HistoryManualMedicine[];
  Formulas: Formula[];
  Exames: Exam[];
  Atestados: MedicalCertificate[];
  DataCriacao: string;
  Referencia: string;
  Encaminhamentos: MedicalReferral[];
  Relatorios: MedicalReport[];
  Medico: Doctor;
  PodeCancelar: boolean;
  habilitarBotaoMais: boolean;
  cancelados: CANCELLABLE_DOCS[];
}

export interface Doctor {
  idMedico: number;
}

export interface PrescriptionHistory {
  Medico: Doctor;
  Prescricoes: HistoryPrescription[];
  PossuiProximaPagina: boolean;
}

export interface Model {
  id: number;
  nome: string;
  institucional: boolean;
  idPrescricao: number;
  administradorModeloInstitucional: boolean;
  destacado: boolean;
  patrocinado?: boolean;
}

export interface ModelDetailManualMedicine {
  NomeMedicamento: string;
  Posologia: string;
  UsoContinuo: boolean;
  UsoInterno: boolean;
  idPrescricaoMedicamentoManual: number;
  QuantidadeUnidadeFormatada: string;
  Ordem: number | null;
  TipoReceita: PT_PRESCRIPTION_TYPE | null;
  Manipulado: boolean;
}

export interface ModelDetails {
  idPrescricao: number;
  Nome: string;
  MedicamentoManual: Array<ManualMedicine>;
  Medicamento: Array<Medicine>;
  Exame: Array<Exam>;
  Comentario?: string;
  Atestados: Array<MedicalCertificate> | null;
  Encaminhamentos: Array<MedicalReferral> | null;
  Formulas?: Array<Formula>;
  Relatorios: Array<MedicalReport> | null;
  destacado?: boolean;
  patrocinado?: boolean;
  id?: number;
}

interface CreateModelResponse {
  prescriptionId: number;
  name: string;
  prescriptionModelId: number;
}

export interface HistoryDocuments {
  idPrescricao: number;
  url: string;
  assinado: boolean;
  assinatura: string;
  tipoDocumento: string;
}
